import { Suspense, useEffect } from 'react';
import { Outlet, useLocation } from 'react-router-dom';

import Footer from '@/components/layout/footer';
import MainContentWrapper from '@/components/layout/MainContentWrapper';
import { Logger } from '@/utils/logger';

import Navbar from '@/components/layout/Navbar';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import Error404 from '@/pages/error/404';
import { ESCRIPT_ROUTE } from '@/routes/route-paths';
import { ACTOR_TYPE, useSessionHeartbeat } from '@montugroup/circuit-shared';
import { CircularProgressWrapper } from '@montugroup/circuit-shared/components/common/CircularProgressWrapper';
import { useAsyncFileGenerationFeedback } from '@montugroup/circuit-shared/hooks/useAsyncFileGenerationFeedback';
import useUser from '@montugroup/circuit-shared/hooks/user/useUser';
import AuthService from '@montugroup/circuit-shared/services/authentication.service';
import { Box, CircularProgress } from '@montugroup/design-system';
import { BrandThemeProvider, uMeds } from '@montugroup/themes';
import { styled } from '@mui/material';

const logger = new Logger('requireAuth');

const MainContent = styled(Box)(() => ({
  height: '100%',
  padding: '100px 10px 20px 10px',
}));

function RequireAuth() {
  const { data: user, isLoading: userIsLoading } = useUser();
  const shouldUseSessionHeartbeat = useFeatureFlag('auth_enable_session_heartbeat');
  const location = useLocation();
  useSessionHeartbeat({
    enabled: Boolean(user && shouldUseSessionHeartbeat),
    keepAlive: Boolean(user && shouldUseSessionHeartbeat && !location.pathname.startsWith(ESCRIPT_ROUTE)),
  });
  useAsyncFileGenerationFeedback();

  useEffect(() => {
    if (!user && !userIsLoading) {
      logger.info('useEffect logout');
      AuthService.logout({ reason: 'expired' });
    }
  }, [location.pathname, user, userIsLoading]);

  if (!user) {
    logger.info('loadingSpinnerHit', {
      '!user': !user,
      userIsLoading,
    });

    return <CircularProgressWrapper />;
  }

  logger.info('notLoading');

  // Check if user is pharmacist actorType - required for all pharmacist-portal routes
  if (user.actorType !== ACTOR_TYPE.pharmacist) {
    logger.info('User is not pharmacist actorType', {
      pathname: location.pathname,
      'user.id': user.id,
      'user.actorType': user.actorType,
    });

    return <Error404 />;
  }

  // TODO: Refactor layout logic out of the AuthenticatedRoute Component
  return (
    <MainContentWrapper>
      <BrandThemeProvider theme={uMeds}>
        <Navbar />
      </BrandThemeProvider>
      <MainContent id="main-content">
        <Suspense fallback={<CircularProgress />}>
          <Outlet />
        </Suspense>
      </MainContent>
      <Footer />
    </MainContentWrapper>
  );
}

export default RequireAuth;
